import { isAppMode } from "@/platform/app-mode/client"

export const SESSION_WINDOW_MS = 30 * 60 * 1000

const SLIDE_AFTER_MS = SESSION_WINDOW_MS / 10

const KEY_PREFIX = "nelo.session."

interface SessionRecord {
  value: string
  touchedAt: number
}

const memory = new Map<string, SessionRecord>()

const webStorage = (kind: "localStorage" | "sessionStorage"): Storage | undefined => {
  if (typeof window === "undefined") return undefined

  try {
    return window[kind]
  } catch {
    return undefined
  }
}

// Each per-screen WebView the app pushes is its own browsing context with its own
// sessionStorage, but one app shares one localStorage, so only localStorage carries an id
// across a native push, and the stamp keeps it expiring like a session. A browser tab keeps
// the per-tab storage it has always had, where closing the tab ends the session.
const durableStore = () => webStorage("localStorage")

const perTabStore = () => webStorage("sessionStorage")

const durableKey = (key: string) => `${KEY_PREFIX}${key}`

const parseRecord = (raw: string | null): SessionRecord | undefined => {
  if (raw === null) return undefined

  try {
    const parsed = JSON.parse(raw) as Partial<SessionRecord>
    if (typeof parsed.value !== "string" || typeof parsed.touchedAt !== "number") return undefined
    return { value: parsed.value, touchedAt: parsed.touchedAt }
  } catch {
    return undefined
  }
}

const isExpired = (record: SessionRecord) => Date.now() - record.touchedAt > SESSION_WINDOW_MS

export const evictStaleSessionKeys = (): void => {
  const store = durableStore()
  if (!store) return

  try {
    for (const name of Object.keys(store)) {
      if (!name.startsWith(KEY_PREFIX)) continue

      const record = parseRecord(store.getItem(name))
      if (!record || isExpired(record)) store.removeItem(name)
    }
  } catch {
    return
  }
}

let evicted = false

const evictOnce = (): void => {
  if (evicted) return
  evicted = true
  evictStaleSessionKeys()
}

const removeFrom = (store: Storage | undefined, name: string): void => {
  if (!store) return

  try {
    store.removeItem(name)
  } catch {
    return
  }
}

const forget = (key: string): void => {
  memory.delete(key)
  removeFrom(durableStore(), durableKey(key))
  removeFrom(perTabStore(), key)
}

const readDurable = (key: string): string | undefined => {
  const store = durableStore()
  let record: SessionRecord | undefined

  if (store) {
    try {
      record = parseRecord(store.getItem(durableKey(key)))
    } catch {
      record = undefined
    }
  }

  record ??= memory.get(key)
  if (!record) return undefined

  if (isExpired(record)) {
    forget(key)
    return undefined
  }

  if (Date.now() - record.touchedAt > SLIDE_AFTER_MS) writeDurable(key, record.value)
  return record.value
}

const writeDurable = (key: string, value: string): void => {
  const record: SessionRecord = { value, touchedAt: Date.now() }
  memory.set(key, record)

  const store = durableStore()
  if (!store) return

  try {
    store.setItem(durableKey(key), JSON.stringify(record))
  } catch {
    return
  }
}

const readPerTab = (key: string): string | undefined => {
  const store = perTabStore()
  if (!store) return memory.get(key)?.value

  try {
    return store.getItem(key) ?? undefined
  } catch {
    return memory.get(key)?.value
  }
}

const writePerTab = (key: string, value: string): void => {
  memory.set(key, { value, touchedAt: Date.now() })

  const store = perTabStore()
  if (!store) return

  try {
    store.setItem(key, value)
  } catch {
    return
  }
}

export const removeSessionScoped = (key: string): void => forget(key)

// State that must not be shared between screens: each WebView the app pushes has its own sessionStorage.
export const readScreenScoped = (key: string): string | undefined => readPerTab(key)

export const writeScreenScoped = (key: string, value: string): void => writePerTab(key, value)

export const removeScreenScoped = (key: string): void => {
  memory.delete(key)
  removeFrom(perTabStore(), key)
}

export const readSessionScoped = (key: string): string | undefined => {
  evictOnce()
  return isAppMode() ? readDurable(key) : readPerTab(key)
}

export const writeSessionScoped = (key: string, value: string): void => {
  evictOnce()

  if (isAppMode()) {
    writeDurable(key, value)
    return
  }

  writePerTab(key, value)
}
